// Barre d'action en bas de l'onglet Récit, au-dessus des onglets. Une seule zone à la fois : la question la plus
// urgente, en bandeau replié (« Répondre ▾ ») qui se déplie en carte complète ; sinon agonie, sommeil, voyage ou
// combat, sinon la saisie de l'action avec les suggestions du Conteur, l'énergie et le bouton Dormir. Le brouillon
// reste dans sessionStorage.
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import type { CharacterView, ClientView, Id, Sollicitation, Urgency } from "../../shared/types";
import { send, useAppState } from "../store";
import { SollicitationCard, formatClock, formatDuration, useLiveNow } from "./SollicitationCard";
import "./ActionBar.css";

export interface ActionBarProps { view: ClientView }

const MAX_ACTION = 500;
const MAX_LINES = 4;
const DRAFT_PREFIX = "veillee.brouillon.";
const URGENCY_RANK: Record<Urgency, number> = { immediat: 0, pressant: 1, tranquille: 2 };

export function ActionBar(props: ActionBarProps) {
  const { view } = props;
  const app = useAppState();
  const c = view.character;
  if (!c || c.lifeState === "mort") return null;
  return (
    <section class="action-bar" aria-label="Ton action">
      {currentBlock(view, c, view.gmBusy || app.gm.busy)}
    </section>
  );
}

function currentBlock(view: ClientView, c: CharacterView, gmBusy: boolean) {
  const questions = openQuestions(view.sollicitations);
  const [premiere] = questions;
  // La clé suit la question la plus urgente : une nouvelle question s'affiche d'abord repliée.
  if (premiere) return <SollicitationsBlock key={premiere.id} items={questions} now={view.now} />;
  if (c.lifeState === "agonie") return <AgonyBlock c={c} now={view.now} />;
  if (c.lifeState === "endormi") return <SleepBlock c={c} />;
  if (c.lifeState === "en_route" || c.travel) return <TravelBlock c={c} now={view.now} />;
  if (view.combat?.status === "en_cours") return <CombatReminder />;
  return <ComposeBlock key={c.id} c={c} gmBusy={gmBusy} />;
}

/** Questions ouvertes, la plus urgente d'abord, puis la plus proche échéance. */
function openQuestions(list: Sollicitation[]): Sollicitation[] {
  return list
    .filter((s) => s.status === "ouverte")
    .sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] || a.deadline - b.deadline);
}

/**
 * Questions ouvertes : la plus urgente se replie en bandeau (« +N autres » s'il y en a d'autres) et se déplie en carte.
 * Dépliée, les autres s'empilent dessous en bandeaux, pour y répondre aussi.
 */
function SollicitationsBlock(props: { items: Sollicitation[]; now: number }) {
  const { items, now } = props;
  const [ouverte, setOuverte] = useState(false);
  const [choisie, setChoisie] = useState<Id | null>(null);
  const courante = items.find((s) => s.id === choisie) ?? items[0];
  if (!courante) return null;
  const autres = items.filter((s) => s.id !== courante.id);

  if (!ouverte) {
    return (
      <div class="action-bar-questions">
        <SollicitationCard compact s={courante} now={now} autres={autres.length} onExpand={() => setOuverte(true)} />
      </div>
    );
  }
  return (
    <div class="action-bar-questions">
      <SollicitationCard key={courante.id} s={courante} now={now} onCollapse={() => setOuverte(false)} />
      {autres.length > 0 ? (
        <ul class="action-bar-autres" aria-label="Autres questions">
          {autres.map((s) => (
            <li key={s.id}>
              <SollicitationCard compact s={s} now={now} onExpand={() => setChoisie(s.id)} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function AgonyBlock(props: { c: CharacterView; now: number }) {
  const { c, now } = props;
  const live = useLiveNow(now);
  const left = c.agonieUntil === null ? 0 : c.agonieUntil - live;
  const canCling = c.energy >= 1;
  return (
    <div class="action-bar-card action-bar-card--agony">
      <h2 class="action-bar-card-title">Tu agonises…</h2>
      <p class="action-bar-card-text">
        {left > 0 ? `Il te reste ${formatDuration(left)} avant que la mort ne t'emporte.` : "Le souffle s'éteint…"}
      </p>
      <p class="action-bar-card-hint">Un compagnon présent peut te soigner.</p>
      <div class="action-bar-actions">
        <button type="button" class="btn btn-primary" disabled={!canCling} onClick={() => send({ t: "cling_to_life" })}>
          S'accrocher à la vie (1⚡)
        </button>
      </div>
      {canCling ? null : <p class="action-bar-note">Il te faut au moins 1 ⚡ pour t'accrocher.</p>}
    </div>
  );
}

function SleepBlock(props: { c: CharacterView }) {
  const { c } = props;
  return (
    <div class="action-bar-card action-bar-card--sleep">
      <h2 class="action-bar-card-title">
        {c.sleepUntil !== null ? `Tu dors jusqu'à ${formatClock(c.sleepUntil)}` : "Tu dors…"}
      </h2>
      <p class="action-bar-card-text">Ta mana remonte doucement pendant ton sommeil.</p>
      <div class="action-bar-actions">
        <button type="button" class="btn btn-primary" onClick={() => send({ t: "wake" })}>Se réveiller</button>
      </div>
    </div>
  );
}

function TravelBlock(props: { c: CharacterView; now: number }) {
  const { c, now } = props;
  const live = useLiveNow(now);
  const t = c.travel;
  const total = t ? t.arriveAt - t.departAt : 0;
  const ratio = t && total > 0 ? Math.min(1, Math.max(0, (live - t.departAt) / total)) : 0;
  const percent = Math.round(ratio * 100);
  const left = t ? t.arriveAt - live : 0;
  return (
    <div class="action-bar-card action-bar-card--travel">
      <h2 class="action-bar-card-title">{t ? `Tu marches vers ${t.toName}` : "Tu es en chemin"}</h2>
      {t ? (
        <>
          <div
            class="bar action-bar-progress"
            role="progressbar"
            aria-label="Progression du trajet"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div class="bar-fill" style={{ width: `${percent}%` }} />
          </div>
          <p class="action-bar-card-text">{left > 0 ? `Arrivée dans ${formatDuration(left)}.` : "Tu arrives presque…"}</p>
        </>
      ) : null}
    </div>
  );
}

function CombatReminder() {
  return (
    <div class="action-bar-card action-bar-card--combat">
      <h2 class="action-bar-card-title">Combat en cours ↑</h2>
      <p class="action-bar-card-text">Ton action se choisit dans le panneau de combat, au-dessus.</p>
    </div>
  );
}

function ComposeBlock(props: { c: CharacterView; gmBusy: boolean }) {
  const { c, gmBusy } = props;
  const draftKey = `${DRAFT_PREFIX}${c.id}`;
  const [text, setText] = useState(() => readDraft(draftKey));
  const taRef = useRef<HTMLTextAreaElement>(null);
  const busy = c.busy || gmBusy;
  const canSend = !busy && text.trim() !== "";

  useLayoutEffect(() => fitHeight(taRef.current), [text]);

  function update(value: string) {
    const v = value.slice(0, MAX_ACTION);
    setText(v);
    writeDraft(draftKey, v);
  }

  function submit() {
    const value = text.trim();
    if (busy || value === "") return;
    send({ t: "action", text: value });
    writeDraft(draftKey, "");
    setText("");
  }

  const pips = Array.from({ length: c.energyMax }, (_, i) => (
    <span key={i} class={i < c.energy ? "action-bar-pip action-bar-pip--on" : "action-bar-pip"} />
  ));

  return (
    <div class="action-bar-compose-block">
      {c.suggestions.length > 0 ? (
        <div class="action-bar-suggestions" role="group" aria-label="Suggestions d'actions">
          {c.suggestions.map((sug) => (
            <button key={sug} type="button" class="chip action-bar-chip" disabled={busy} onClick={() => update(sug)}>
              {sug}
            </button>
          ))}
        </div>
      ) : null}

      {busy ? (
        <p class="action-bar-busy" role="status">
          {gmBusy ? "Le Conteur écrit…" : "Ton action est en cours de résolution…"}
        </p>
      ) : null}
      {c.lifeState === "egare" ? (
        <p class="action-bar-note">Tu t'étais égaré dans le brouillard. Ta prochaine action te ramène dans le récit.</p>
      ) : null}

      <div class="action-bar-compose">
        <textarea
          ref={taRef}
          class="textarea action-bar-input"
          rows={1}
          maxLength={MAX_ACTION}
          placeholder="Que fais-tu ?"
          aria-label="Que fais-tu ?"
          value={text}
          disabled={busy}
          onInput={(e) => update(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button type="button" class="btn btn-primary action-bar-send" disabled={!canSend} onClick={submit}>
          Envoyer
        </button>
      </div>

      <div class="action-bar-meta">
        <div class="action-bar-energy">
          <span class="action-bar-energy-label">⚡ {c.energy}/{c.energyMax}</span>
          <span class="action-bar-pips" aria-hidden="true">{pips}</span>
        </div>
        <button type="button" class="btn btn-ghost action-bar-sleep" disabled={c.busy} onClick={() => send({ t: "sleep" })}>
          😴 Dormir
        </button>
      </div>

      <div class="action-bar-foot">
        <p class="action-bar-note">
          {c.energy > 0 ? "Une action coûte 0 à 2 ⚡." : "Épuisé : seules les actions sans coût sont permises."}
        </p>
        {text.length >= 400 ? <span class="action-bar-count">{text.length}/{MAX_ACTION}</span> : null}
      </div>
    </div>
  );
}

function readDraft(key: string): string {
  try {
    return sessionStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeDraft(key: string, value: string): void {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // stockage indisponible (navigation privée, quota) : le brouillon reste en mémoire seulement
  }
}

/** Hauteur du champ : suit le texte, de 1 à MAX_LINES lignes. */
function fitHeight(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = "auto";
  const cs = getComputedStyle(el);
  const num = (v: string) => parseFloat(v) || 0;
  const border = num(cs.borderTopWidth) + num(cs.borderBottomWidth);
  const padding = num(cs.paddingTop) + num(cs.paddingBottom);
  const line = num(cs.lineHeight) || num(cs.fontSize) * 1.4;
  const wanted = el.scrollHeight + border;
  const cap = line * MAX_LINES + padding + border;
  el.style.height = `${Math.min(wanted, cap)}px`;
  el.style.overflowY = wanted > cap ? "auto" : "hidden";
}
